import React, { useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import { track } from '../../analytics/utils';
import {
  PrivacyPolicyContainer,
  Heading,
  Paragraph,
  OrderedList,
  ListItem,
  TextWrapper,
  InteractiveTextWrapper,
  OpenModalButtonText,
} from './styles';
import {
  AnimatedSlideDownView,
  Border,
  InteractiveText,
  Margin,
  Media,
  Padding,
  PlusOrPlusSign,
  Queue,
  Text,
} from '../../components';

export const questionsAndAnswers = [
  {
    question: 'How do I know if you received my application?',
    answer:
      'When you apply for a role through our postings on Greenhouse you’ll receive an auto-reply once your application has been submitted. If for some reason you don’t receive this auto-reply, please check your spam. If you don’t see it there, reach out to careers@glossier.com and we can verify that we have your application in our system.',
  },
  {
    question:
      'How will I know if I have been selected to move forward with the process?',
    answer:
      'If you have been selected to move forward with the interview process, someone from our team will reach out to you via email to schedule next steps.',
  },
  {
    question: 'Will you tell me when a role has been filled?',
    answer:
      'When we fill a role, we’ll send an email to let you know the position is no longer available. Sometimes it takes a while to fill positions, so please be patient if you haven’t heard from us!',
  },
  {
    question: 'Do you have internship roles available?',
    answer: `While we don't currently have a formal internship program, we do offer occasional internships depending on the needs of the business. If an internship position does become available, it will be posted here.`,
  },
  {
    question: 'Do you have opportunities for new grads?',
    answer:
      'Yes! But not all the time. Our open positions change on an almost weekly basis depending on business need. The “Qualifications” section of the job description will indicate the required amount of experience for the role. If you don’t see any entry-level roles currently available, keep checking back.',
  },
  {
    question: `I'm interested in a role, but I can't work from New York or Seattle. What remote positions are available?`,
    answer:
      'We occasionally have remote opportunities available with the United States, most frequently for our gTEAM and Engineering teams. All our roles on the careers page specify where they are located, so please keep an eye out to see if they are remote.',
  },
  {
    question: 'What can I expect during the interview process?',
    answer:
      "We use a structured hiring process to ensure that every candidate is asked the same questions and assessed similarly. Through our process, we evaluate core skills and use our values to determine whether Glossier is the place where you'll be able to do your best work. So take a look ahead of time and let us know which values resonate with you most!",
  },
  {
    question:
      'I’m interested in a role, but I don’t check off all the qualifications. Should I still apply?',
    answer:
      'Yes! At Glossier, we are building the future beauty company, and in order to do so, we believe our internal team should reflect the world we want to live in—one where all voices are heard, listened to and valued. That means looking beyond specific qualifications or backgrounds or education to better understand, will you thrive in this role at Glossier? If you’re interested in one of the roles on our team, check out the 6-month and 12-month expectations in the job description. If this seems reasonable given your skillset and previous experience, please make sure to bring it up during our conversations with you!',
  },
  {
    question: 'Do you have a candidate privacy policy?',
    answer: 'Yes, you can find the details',
    privacyPolicyText: 'here.',
    privacyPolicyURL:
      'https://cdn.shopify.com/s/files/1/0627/9164/7477/files/Glossier_Life_Applicant_Privacy_Policy.pdf?v=1724879492',
  },
];

export const ExpandableQuestionView = (props) => {
  let { questionAndAnswer, mobile } = props;
  let {
    question,
    answer,
    privacyPolicyText,
    privacyPolicyURL,
  } = questionAndAnswer || {
    question: 'No question is provided.',
    answer: 'No answer is provided.',
  };
  const [visible, setVisible] = useState(false);

  const openPrivacyPolicy = () => {
    window.open(privacyPolicyURL, '_blank').focus();
  };

  return (
    <>
      <Media greaterThanOrEqual="md">
        <Border
          borderSize={mobile ? [1, 0, 0, 0] : [0, 0, 1, 0]}
          borderRole="grey3"
        >
          <Padding size={[6, 4]}>
            <Queue size={8} justifyContent="space-between">
              <TextWrapper>
                <Text textType="primary1" align="left">
                  {question}
                </Text>
              </TextWrapper>
              <InteractiveTextWrapper
                onClick={() => {
                  track(`FAQ ${visible ? 'Open' : 'Closed'}`, {
                    question: question,
                  });
                  setVisible(!visible);
                }}
              >
                {visible ? (
                  <InteractiveText
                    align="right"
                    vertical="flex-top"
                    textType="primary2"
                    nowrap
                    opacity={0.5}
                    underlined
                  >
                    View less
                  </InteractiveText>
                ) : (
                  <InteractiveText
                    align="right"
                    vertical="text-top"
                    textType="primary2"
                    nowrap
                    opacity={0.5}
                    underlined
                  >
                    View more
                  </InteractiveText>
                )}
              </InteractiveTextWrapper>
            </Queue>

            <AnimatedSlideDownView visible={visible}>
              <Margin size={[4, 0, 0, 0]}>
                <Text textType="primary2" align="left">
                  {answer}&nbsp;
                  {privacyPolicyText && (
                    <OpenModalButtonText onClick={() => openPrivacyPolicy()}>
                      {privacyPolicyText}
                    </OpenModalButtonText>
                  )}
                </Text>
              </Margin>
            </AnimatedSlideDownView>
          </Padding>
        </Border>
      </Media>

      <Media at="sm">
        <Border
          borderSize={mobile ? [1, 0, 0, 0] : [0, 0, 1, 0]}
          borderRole="grey3"
        >
          <Padding size={[5, 0]}>
            <Queue
              size={8}
              justifyContent="space-between"
              onClick={() => setVisible(!visible)}
            >
              <Text textType="primary1" align="left">
                {question}
              </Text>
              <div>
                <PlusOrPlusSign open={!visible} height={16} width={16} />
              </div>
            </Queue>

            <AnimatedSlideDownView visible={visible}>
              <Margin size={[4, 0, 0, 0]}>
                <Text textType="primary2" align="left">
                  {answer}&nbsp;
                  {privacyPolicyText && (
                    <OpenModalButtonText onClick={() => openPrivacyPolicy()}>
                      {privacyPolicyText}
                    </OpenModalButtonText>
                  )}
                </Text>
              </Margin>
            </AnimatedSlideDownView>
          </Padding>
        </Border>
      </Media>
    </>
  );
};
